import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import MainPanelContent from "../components/MainPanelContent";
import { SideDrawer } from "../../../components/SideDrawer";
import {
  useModelCacheDetails,
  useModelCaches,
} from "../../../hooks/useModelCaches";
import ModelFilterPanel, { FILTER_PANEL_WIDTH } from "./ModelFilterPanel";
import ModelAdminPanel from "./ModelAdminPanel";
import ModelCard, {
  ModelDetails,
  bestRevision,
  buildHuggingFaceUrl,
  getParameterCount,
} from "./ModelCard";
import {
  ACCENT,
  FONT,
  ICON,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
  WEIGHT,
} from "../../../theme/tokens";

const SORT_OPTIONS = [
  { value: "updated", label: "Recently updated" },
  { value: "name", label: "Name" },
  { value: "size", label: "Largest" },
  { value: "revisions", label: "Most revisions" },
];

const PUBLIC_MODEL_PAGE_SIZE = 30;
const ADMIN_MODEL_PAGE_SIZE = 15;

// Responsive card grid that fills the space beside the filter panel.
const MODEL_GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
  gap: { xs: 2, sm: 2.5 },
  alignItems: "stretch",
};

function modelSearchText(model) {
  return [
    model?.repo_id,
    model?.repo_type,
    ...(model?.revisions || []).flatMap((revision) => [
      revision.commit_hash,
      ...(revision.refs || []),
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function getOrganization(model) {
  return String(model?.repo_id || "").split("/")[0] || "unknown";
}

function buildFacetOptions(models, getValue) {
  const counts = new Map();
  models.forEach((model) => {
    const value = getValue(model);
    if (!value) return;
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, label: value, count }))
    .sort(
      (left, right) =>
        right.count - left.count || left.label.localeCompare(right.label),
    );
}

function ModelPagination({ page, pageCount, show, onChange }) {
  if (!show) return null;

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Pagination
        page={page}
        count={pageCount}
        onChange={(_, nextPage) => onChange(nextPage)}
        color="primary"
        shape="rounded"
        siblingCount={1}
        boundaryCount={1}
      />
    </Box>
  );
}

ModelPagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  show: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function Models({ is_admin_page = false }) {
  const { models, cacheInfo, loading, error, refreshModels } = useModelCaches();
  const {
    detailsByRepo,
    errorsByRepo: detailErrors,
    loadModelDetail,
    loadingRepo: detailLoadingRepo,
  } = useModelCacheDetails();
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("updated");
  const [page, setPage] = useState(1);
  const [activeDetailRepo, setActiveDetailRepo] = useState("");
  const [filters, setFilters] = useState({
    pipelineTag: "",
    organization: "",
    parameterRange: null,
  });
  const [deletingRepo, setDeletingRepo] = useState("");
  const adminPanelRef = useRef(null);
  const modelPageSize = is_admin_page
    ? ADMIN_MODEL_PAGE_SIZE
    : PUBLIC_MODEL_PAGE_SIZE;

  const handleFilterChange = (nextFilters) => {
    const range = nextFilters.parameterRange;
    const coversFullRange =
      !range || (range[0] <= 0 && range[1] >= maxParameterCount);
    setFilters({
      ...nextFilters,
      parameterRange: coversFullRange ? null : range,
    });
    setPage(1);
  };

  const handleOpenDetails = (repoId) => {
    setActiveDetailRepo(repoId);
    loadModelDetail(repoId);
  };

  const handleCloseDetails = () => setActiveDetailRepo("");

  const handleDeleteModel = (repoId) => {
    if (!is_admin_page || !repoId || deletingRepo) return;
    adminPanelRef.current?.deleteModel(repoId);
  };

  const maxParameterCount = useMemo(
    () =>
      models.reduce(
        (maxCount, model) => Math.max(maxCount, getParameterCount(model)),
        0,
      ),
    [models],
  );

  const pipelineTags = useMemo(
    () =>
      buildFacetOptions(models, (model) => model?.pipeline_tag || "unknown"),
    [models],
  );

  const organizations = useMemo(
    () => buildFacetOptions(models, getOrganization),
    [models],
  );

  const activeModel = useMemo(
    () => models.find((model) => model.repo_id === activeDetailRepo) || null,
    [models, activeDetailRepo],
  );

  const visibleModels = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const parameterRange = filters.parameterRange ?? [0, maxParameterCount];
    const [minParameterCount, maxParameterLimit] = parameterRange;
    const filteredByQuery = normalizedQuery
      ? models.filter((model) =>
          modelSearchText(model).includes(normalizedQuery),
        )
      : [...models];

    const filtered = filteredByQuery.filter((model) => {
      if (filters.pipelineTag) {
        const pipelineTag = model?.pipeline_tag || "unknown";
        if (pipelineTag !== filters.pipelineTag) return false;
      }
      if (
        filters.organization &&
        getOrganization(model) !== filters.organization
      ) {
        return false;
      }
      const parameterCount = getParameterCount(model);
      return (
        parameterCount >= minParameterCount &&
        parameterCount <= maxParameterLimit
      );
    });

    filtered.sort((left, right) => {
      if (sortBy === "name") {
        return String(left.repo_id || "").localeCompare(
          String(right.repo_id || ""),
        );
      }
      if (sortBy === "size") {
        return Number(right.size_on_disk || 0) - Number(left.size_on_disk || 0);
      }
      if (sortBy === "revisions") {
        return (right.revisions?.length || 0) - (left.revisions?.length || 0);
      }
      const rightTime =
        bestRevision(right)?.last_modified ?? right.last_modified ?? 0;
      const leftTime =
        bestRevision(left)?.last_modified ?? left.last_modified ?? 0;
      return Number(rightTime || 0) - Number(leftTime || 0);
    });

    return filtered;
  }, [filters, maxParameterCount, models, query, sortBy]);

  const pageCount = Math.max(
    1,
    Math.ceil(visibleModels.length / modelPageSize),
  );

  const pageModels = useMemo(() => {
    const start = (page - 1) * modelPageSize;
    return visibleModels.slice(start, start + modelPageSize);
  }, [modelPageSize, page, visibleModels]);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, pageCount));
  }, [pageCount]);

  const showPagination = !loading && visibleModels.length > modelPageSize;

  return (
    <MainPanelContent
      title={
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <span>Models</span>
          <Typography
            component="span"
            sx={{ fontSize: FONT.md, color: INK_SECONDARY, fontWeight: 500 }}
          >
            {models.length.toLocaleString()}
          </Typography>
        </Box>
      }
      subtitle={
        "Cached Hugging Face models available for EFabric experiments. " +
        (is_admin_page
          ? "Admins can inspect and delete cached model entries."
          : "Click model names to browse their Hugging Face pages.")
      }
      maxWidth={null}
      contentGap={2}
      headerMb={1.5}
    >
      <Box
        sx={{
          display: { xs: "flex", lg: "grid" },
          flexDirection: "column",
          gridTemplateColumns: `${FILTER_PANEL_WIDTH}px minmax(0, 1fr)`,
          alignItems: "start",
          gap: { xs: 2, lg: 2.5 },
          pr: { xs: 1, sm: 1.5 },
          width: "100%",
        }}
      >
        <ModelFilterPanel
          filters={{
            ...filters,
            parameterRange: filters.parameterRange ?? [0, maxParameterCount],
          }}
          maxParameterCount={maxParameterCount}
          onChange={handleFilterChange}
          organizations={organizations}
          pipelineTags={pipelineTags}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
            width: "100%",
          }}
        >
          {is_admin_page && (
            <ModelAdminPanel
              ref={adminPanelRef}
              onRefreshModels={refreshModels}
              onDeletingRepoChange={setDeletingRepo}
              onModelDeleted={(repoId) => {
                if (activeDetailRepo === repoId) {
                  setActiveDetailRepo("");
                }
              }}
            />
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Search cached models"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: ICON, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: "1 1 320px",
                maxWidth: 680,
                "& .MuiOutlinedInput-root": {
                  borderRadius: `${RADIUS.sm}px`,
                  bgcolor: SURFACE,
                },
              }}
            />

            <Select
              size="small"
              value={sortBy}
              onChange={(event) => {
                setSortBy(event.target.value);
                setPage(1);
              }}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <SortRoundedIcon sx={{ color: ICON, fontSize: 18 }} />
                </InputAdornment>
              }
              sx={{
                width: { xs: "100%", sm: 220 },
                borderRadius: `${RADIUS.sm}px`,
                bgcolor: SURFACE,
                fontSize: FONT.sm,
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>

            {!loading && (
              <Typography
                sx={{
                  fontSize: FONT.sm,
                  color: INK_MUTED,
                  ml: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                {visibleModels.length === models.length
                  ? `${models.length.toLocaleString()} model${models.length === 1 ? "" : "s"}`
                  : `${visibleModels.length.toLocaleString()} of ${models.length.toLocaleString()} models`}
              </Typography>
            )}
          </Box>

          <ModelPagination
            page={page}
            pageCount={pageCount}
            show={showPagination}
            onChange={setPage}
          />

          {cacheInfo?.warnings?.length > 0 && (
            <Alert severity="warning">{cacheInfo.warnings.join(" ")}</Alert>
          )}

          {error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={refreshModels}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={MODEL_GRID_SX}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={168}
                  sx={{
                    width: "100%",
                    borderRadius: `${RADIUS.md}px`,
                  }}
                />
              ))}
            </Box>
          ) : visibleModels.length ? (
            <>
              <Box sx={MODEL_GRID_SX}>
                {pageModels.map((model) => (
                  <ModelCard
                    key={model.repo_id}
                    model={model}
                    active={activeDetailRepo === model.repo_id}
                    onOpenDetails={handleOpenDetails}
                    loadingDetail={detailLoadingRepo === model.repo_id}
                    is_admin_page={is_admin_page}
                    onDelete={handleDeleteModel}
                    deleting={deletingRepo === model.repo_id}
                  />
                ))}
              </Box>
              <ModelPagination
                page={page}
                pageCount={pageCount}
                show={showPagination}
                onChange={setPage}
              />
            </>
          ) : (
            <Alert severity="info">
              {query
                ? "No cached models match your search."
                : "No cached models found."}
            </Alert>
          )}
        </Box>
      </Box>

      <SideDrawer
        open={Boolean(activeDetailRepo)}
        onClose={handleCloseDetails}
        title={activeModel?.repo_id || "Model details"}
        optionalHeader={
          activeModel && (
            <Link
              href={buildHuggingFaceUrl(activeModel.repo_id)}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                color: ACCENT,
                fontSize: FONT.sm,
                fontWeight: WEIGHT.semibold,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Open on Hugging Face
              <OpenInNewRoundedIcon sx={{ fontSize: 15 }} />
            </Link>
          )
        }
      >
        {activeModel && (
          <ModelDetails
            key={activeDetailRepo}
            model={activeModel}
            detail={detailsByRepo[activeDetailRepo]}
            loading={detailLoadingRepo === activeDetailRepo}
            error={detailErrors[activeDetailRepo]}
          />
        )}
      </SideDrawer>
    </MainPanelContent>
  );
}

Models.propTypes = {
  is_admin_page: PropTypes.bool,
};
